import * as THREE from 'three';

type PointerState = {
  active: boolean;
  id: number | null;
  centerX: number;
  centerY: number;
  radius: number;
};

/**
 * Context-sensitive input.
 *
 * The controller only reports raw axes and edge-triggered actions; `Game`
 * decides what they mean based on the player's station:
 *  - free-roaming: movement axes move the avatar on deck,
 *  - helm: horizontal axis is rudder,
 *  - sail station: vertical axis raises/lowers, horizontal rotates.
 *
 * Touch uses the same virtual stick for all contexts; the action button maps to
 * the interact key. The old dash action is gone.
 */
export class InputController {
  private readonly keys = new Set<string>();
  private readonly pointer = new THREE.Vector2();
  private readonly keyVector = new THREE.Vector2();
  private readonly pointerState: PointerState = {
    active: false,
    id: null,
    centerX: 0,
    centerY: 0,
    radius: 1,
  };

  private interactPressed = false;
  private firePortPressed = false;
  private fireStarboardPressed = false;
  private toggleCameraPressed = false;
  private anchorPressed = false;

  private readonly onKeyDown = (event: KeyboardEvent) => {
    this.keys.add(event.code);
    if (event.code === 'KeyE') this.interactPressed = true;
    if (event.code === 'KeyQ') this.firePortPressed = true;
    if (event.code === 'KeyR') this.fireStarboardPressed = true;
    if (event.code === 'KeyV') this.toggleCameraPressed = true;
    if (event.code === 'KeyF') this.anchorPressed = true;
    if (event.code.startsWith('Arrow')) event.preventDefault();
  };

  private readonly onKeyUp = (event: KeyboardEvent) => {
    this.keys.delete(event.code);
  };

  private readonly onStickDown = (event: PointerEvent) => {
    event.preventDefault();
    const rect = this.stick.getBoundingClientRect();
    this.pointerState.active = true;
    this.pointerState.id = event.pointerId;
    this.pointerState.centerX = rect.left + rect.width / 2;
    this.pointerState.centerY = rect.top + rect.height / 2;
    this.pointerState.radius = rect.width * 0.42;
    try {
      this.stick.setPointerCapture(event.pointerId);
    } catch {
      // Synthetic test events do not always have a capturable pointer id.
    }
    this.updatePointer(event.clientX, event.clientY);
  };

  private readonly onStickMove = (event: PointerEvent) => {
    if (!this.pointerState.active || event.pointerId !== this.pointerState.id) return;
    event.preventDefault();
    this.updatePointer(event.clientX, event.clientY);
  };

  private readonly onStickUp = (event: PointerEvent) => {
    if (event.pointerId !== this.pointerState.id) return;
    event.preventDefault();
    this.pointerState.active = false;
    this.pointerState.id = null;
    this.pointer.set(0, 0);
    this.updateKnob();
  };

  private readonly onActionDown = (event: PointerEvent) => {
    event.preventDefault();
    this.interactPressed = true;
  };

  constructor(
    private readonly stick: HTMLElement,
    private readonly knob: HTMLElement,
    private readonly actionButton: HTMLElement,
  ) {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    this.stick.addEventListener('pointerdown', this.onStickDown);
    this.stick.addEventListener('pointermove', this.onStickMove);
    this.stick.addEventListener('pointerup', this.onStickUp);
    this.stick.addEventListener('pointercancel', this.onStickUp);
    this.actionButton.addEventListener('pointerdown', this.onActionDown);
  }

  /** Normalised movement vector for free-roaming on deck. */
  readMove(target: THREE.Vector2): THREE.Vector2 {
    this.keyVector.set(0, 0);
    if (this.keys.has('KeyA') || this.keys.has('ArrowLeft')) this.keyVector.x -= 1;
    if (this.keys.has('KeyD') || this.keys.has('ArrowRight')) this.keyVector.x += 1;
    if (this.keys.has('KeyW') || this.keys.has('ArrowUp')) this.keyVector.y -= 1;
    if (this.keys.has('KeyS') || this.keys.has('ArrowDown')) this.keyVector.y += 1;

    target.copy(this.keyVector).add(this.pointer);
    if (target.lengthSq() > 1) target.normalize();
    return target;
  }

  /**
   * Snapshot of every key currently held (`KeyboardEvent.code`), sorted for
   * deterministic diagnostics and overlay output. Returns a copy so callers
   * cannot mutate the live set. Read-only by construction: the overlay reports
   * the exact set the real input path acts on, never a second listener's view.
   */
  readPressedKeys(): string[] {
    return Array.from(this.keys).sort();
  }

  /** Horizontal axis in [-1, 1]: rudder at the helm, sail rotation at a sail. */
  readHorizontal(): number {
    let value = this.pointer.x;
    if (this.keys.has('KeyA') || this.keys.has('ArrowLeft')) value -= 1;
    if (this.keys.has('KeyD') || this.keys.has('ArrowRight')) value += 1;
    return Math.max(-1, Math.min(1, value));
  }

  /** Vertical axis in [-1, 1]: negative raises a sail, positive lowers it. */
  readVertical(): number {
    let value = this.pointer.y;
    if (this.keys.has('KeyW') || this.keys.has('ArrowUp')) value -= 1;
    if (this.keys.has('KeyS') || this.keys.has('ArrowDown')) value += 1;
    return Math.max(-1, Math.min(1, value));
  }

  /** True while the sail-rotation modifier (either Shift key) is held. */
  readRotationModifier(): boolean {
    return this.keys.has('ShiftLeft') || this.keys.has('ShiftRight');
  }

  /** True once per interact press (E key or touch action button). */
  consumeInteract(): boolean {
    if (!this.interactPressed) return false;
    this.interactPressed = false;
    return true;
  }

  /** True once per port-broadside fire press (Q key). */
  consumeFirePort(): boolean {
    if (!this.firePortPressed) return false;
    this.firePortPressed = false;
    return true;
  }

  /** True once per starboard-broadside fire press (R key). */
  consumeFireStarboard(): boolean {
    if (!this.fireStarboardPressed) return false;
    this.fireStarboardPressed = false;
    return true;
  }

  /** True once per camera-view toggle press (V key). */
  consumeToggleCamera(): boolean {
    if (!this.toggleCameraPressed) return false;
    this.toggleCameraPressed = false;
    return true;
  }

  /**
   * True once per anchor press (F key): drop the shared anchor, or weigh it
   * when it is down. Any crew member may operate it, from anywhere, at any
   * time — the server applies the same no-cost/no-limit rule.
   */
  consumeAnchorToggle(): boolean {
    if (!this.anchorPressed) return false;
    this.anchorPressed = false;
    return true;
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    this.stick.removeEventListener('pointerdown', this.onStickDown);
    this.stick.removeEventListener('pointermove', this.onStickMove);
    this.stick.removeEventListener('pointerup', this.onStickUp);
    this.stick.removeEventListener('pointercancel', this.onStickUp);
    this.actionButton.removeEventListener('pointerdown', this.onActionDown);
  }

  private updatePointer(clientX: number, clientY: number): void {
    const dx = clientX - this.pointerState.centerX;
    const dy = clientY - this.pointerState.centerY;
    this.pointer.set(dx / this.pointerState.radius, dy / this.pointerState.radius);
    if (this.pointer.lengthSq() > 1) this.pointer.normalize();
    this.updateKnob();
  }

  private updateKnob(): void {
    const distance = 38;
    this.knob.style.transform = `translate(calc(-50% + ${this.pointer.x * distance}px), calc(-50% + ${this.pointer.y * distance}px))`;
  }
}

/** Deck-local unit move direction resolved from a screen-space input vector. */
export interface DeckMoveVector {
  /** Unit bow-axis component in ship-local deck space (+X = toward the bow). */
  dx: number;
  /** Unit starboard-axis component in ship-local deck space (+Z = starboard). */
  dz: number;
}

/**
 * Convert the helm input axis (screen frame, right = +1) into the yaw-frame
 * rudder order broadcast as `helm_input.turn`.
 *
 * The two frames are opposite. The hull's bow is local +X and a *larger*
 * `rotationY` swings it toward port (local -Z, per `resolveDeckLocalMove`'s
 * "positive toward port" convention), while the input axis calls the player's
 * right positive. So a rightward input must be sent as a negative (starboard)
 * rudder order, or the helm steers the wrong way.
 *
 * Pure on purpose: the conversion is unit-tested without a running server.
 */
export function helmTurnFromInput(horizontal: number): number {
  return -horizontal;
}

/**
 * Convert the sail-rotation input axis (screen frame, right = +1) into the
 * yaw-frame delta broadcast as `sail_rotate.delta`.
 *
 * The two frames are opposite, exactly as for the helm. Positive `sail.angle`
 * (the value the server adds this delta to) rotates the boom toward port
 * (local -Z), while the input axis calls the player's right positive. So a
 * rightward input must be sent as a negative delta to swing the boom toward
 * starboard — the same "right input = starboard" convention the helm and
 * free-roam already follow.
 *
 * Pure on purpose: the conversion is unit-tested without a running server, and
 * it does not touch the physics sign (`normalAngle = yaw + angle` stays
 * authoritative on the server).
 */
export function sailRotateFromInput(horizontal: number): number {
  return -horizontal;
}

/**
 * Resolve a screen-space movement vector into a camera-relative move in the
 * ship's deck frame. Pure and deterministic; used by `Game`'s free-roam branch.
 *
 * Conventions (see `artifacts/avatar-camera-research.md` §2):
 *  - `moveVec` is the normalised screen-space vector from `readMove`
 *    (x = left/right with right = +1, y = forward/back with forward/W = -1).
 *  - `shipHeading` is the ship's world yaw in radians (0 = bow +X, positive
 *    toward port, matching `Euler(pitch, yaw, roll, 'YXZ')`).
 *  - `cameraYaw` is the camera's world yaw in the same convention
 *    (`shipHeading + CameraRig.movementYaw`).
 *
 * The ship heading cancels out of the returned deck-space direction because the
 * camera yaw is measured in the same rotating deck frame — so `ψ = 0` (camera
 * straight behind the bow) reproduces today's tank mapping exactly.
 */
export function resolveDeckLocalMove(
  moveVec: { x: number; y: number },
  shipHeading: number,
  cameraYaw: number,
): DeckMoveVector {
  const forward = -moveVec.y; // W → +1 (toward the bow)
  const right = moveVec.x; // D → +1 (toward starboard)
  const psi = cameraYaw - shipHeading; // deck-relative camera yaw
  const cosPsi = Math.cos(psi);
  const sinPsi = Math.sin(psi);
  return {
    dx: forward * cosPsi + right * sinPsi,
    dz: -forward * sinPsi + right * cosPsi,
  };
}
